import type { ReactNode } from "react";
import type { ColumnDef } from "./types";

/**
 * 8 種 formatter 的純函式版本（供畫面渲染與 CSV 匯出共用同一套邏輯，
 * 避免兩處各寫一份導致顯示與匯出不一致）。
 */

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** 接受 Firestore Timestamp-like（{seconds}/{_seconds}）、ISO 字串、number(ms)、Date。 */
export function toDate(input: unknown): Date | null {
  if (input === null || input === undefined || input === "") return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === "string") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === "object") {
    const obj = input as Record<string, unknown>;
    const secs = (obj.seconds ?? obj._seconds) as number | undefined;
    if (typeof secs === "number") {
      const d = new Date(secs * 1000);
      return isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
}

export function formatDate(input: unknown): string {
  const d = toDate(input);
  if (!d) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function formatDateTime(input: unknown): string {
  const d = toDate(input);
  if (!d) return "";
  return `${formatDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

export function formatCurrency(input: unknown, symbol = "NT$"): string {
  const n = typeof input === "number" ? input : Number(input);
  if (input === null || input === undefined || input === "" || isNaN(n)) return "";
  return `${symbol}${n.toLocaleString("zh-Hant-TW", { maximumFractionDigits: 2 })}`;
}

export function formatBoolean(input: unknown, labels: [string, string] = ["否", "是"]): string {
  const truthy = input === true || input === 1 || input === "1" || input === "true";
  return truthy ? labels[1] : labels[0];
}

export function formatText(input: unknown): string {
  if (input === null || input === undefined) return "";
  return String(input);
}

/** 純文字化的值（不含 JSX），CSV 匯出與全域搜尋皆走這條，確保「畫面看到什麼、篩得到什麼、匯出什麼」一致。 */
export function plainFormattedValue<T>(column: ColumnDef<T>, row: T): string {
  const raw = column.value ? column.value(row) : (row as Record<string, unknown>)[column.key];
  switch (column.format) {
    case "date":
      return formatDate(raw);
    case "datetime":
      return formatDateTime(raw);
    case "currency":
      return formatCurrency(raw, column.currencySymbol);
    case "boolean":
      return formatBoolean(raw, column.booleanLabels ?? ["否", "是"]);
    case "user":
      return raw === null || raw === undefined || raw === ""
        ? ""
        : column.userLabel
          ? column.userLabel(String(raw))
          : String(raw);
    case "ntext":
    case "text":
    case "raw":
    default:
      return formatText(raw);
  }
}

/**
 * 畫面渲染用（raw 走 column.render；其餘走純文字，ntext 截斷）。
 *
 * ⚠️ 這裡的呈現改動一律**不影響** plainFormattedValue —— CSV 匯出與全域搜尋走的是
 * 純文字那條，必須維持「畫面看到什麼、篩得到什麼、匯出什麼」一致。datetime 在畫面
 * 拆成兩行只是視覺排版，匯出仍是單一字串。
 */
export function renderFormattedCell<T>(column: ColumnDef<T>, row: T): ReactNode {
  const raw = column.value ? column.value(row) : (row as Record<string, unknown>)[column.key];
  if (column.render) return column.render(row, raw);
  if (column.format === "raw") return formatText(raw);
  if (column.format === "ntext") {
    // 超過 2～3 行截斷（依密度），title 帶全文——影片描述那種長文原本會把列高撐得參差
    const full = formatText(raw);
    return (
      <span className="cui-dt-cell-ntext" title={full || undefined}>
        {full}
      </span>
    );
  }
  if (column.format === "datetime") {
    // 雙行：日期一行、時間小字一行。省寬度，且同欄位的日期部分能對齊掃視。
    const d = toDate(raw);
    if (!d) return "";
    return (
      <>
        <span className="cui-dt-datetime-date">{formatDate(d)}</span>
        {/*
          兩個 span 之間必須留一個空白文字節點：兩者都是 display:block，這個空白在
          版面上會被吞掉（不影響雙行呈現），但 textContent 因此仍是
          「2024-01-17 09:41:29」而非黏成「2024-01-1709:41:29」——新舊站文字比對
          與 CSV／搜尋都靠這個字串，不能因為視覺換行就少掉分隔。
        */}{" "}
        <span className="cui-dt-datetime-time">
          {pad2(d.getHours())}:{pad2(d.getMinutes())}:{pad2(d.getSeconds())}
        </span>
      </>
    );
  }
  // currency 刻意**不**把幣別符號拆成獨立 <span> 弱化：拆了會讓儲存格文字跨越多個
  // 元素，破壞「一格＝一個文字節點」的可比對性（getByText / 新舊站文字比對都會失手），
  // 換來的只是符號淡一點。靠右對齊與等寬數字才是真正的可讀性來源，由
  // .cui-dt-align-right（tabular-nums）處理，維持走 plainFormattedValue。
  if (column.format === "boolean") {
    // 純文字「啟用/停用」改用 Badge：綠實心圓＝真、灰空心圓＝假。
    // 文字沿用 column.booleanLabels，詞條不變。
    const labels = column.booleanLabels ?? ["否", "是"];
    const truthy = raw === true || raw === 1 || raw === "1" || raw === "true";
    return (
      <span className={`cui-badge cui-badge-${truthy ? "success" : "neutral"}`}>
        {truthy ? labels[1] : labels[0]}
      </span>
    );
  }
  if (column.format === "user") {
    // class 而非 inline style：C7（B3 稽核）需要在斑馬紋列（.cui-dt-table.cui-variant-admin
    // tbody tr:nth-child(even)）另外蓋色才能達 AA，inline style 選不到那個組合選擇器。
    const s = plainFormattedValue(column, row);
    return s ? <span className="cui-dt-cell-user">{s}</span> : "";
  }
  if (column.format === "text") {
    const s = formatText(raw);
    return s ? (
      <span className="cui-dt-cell-truncate" title={s}>
        {s}
      </span>
    ) : (
      ""
    );
  }
  return plainFormattedValue(column, row);
}
